import { Component, Input, OnChanges, OnDestroy, Optional } from '@angular/core';
import { Subject, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { HeatmapImageStore } from './heatmap-images';

/**
 * NK-344 — the Image module: one uploaded picture, drawn the width of its slot.
 *
 * The picture is uploaded from the settings panel and stored like a heatmap's upload, so it comes
 * through the same store: the report's own image route in Navigator, the frozen copy on a share
 * link, and the stand-in services of the previews -- none of which this block needs to know about.
 */
@Component({
  selector: 'app-image-block',
  standalone: false,
  template: `
    @if (url) {
      <img class="image-body" [src]="url" [alt]="altText">
    } @else if (imageId && !failed) {
      <div class="image-empty grey4 nav-font12" role="status">Loading image…</div>
    } @else if (editing) {
      <div class="image-empty grey4 nav-font12">
        <i class="far fa-image mr-1" aria-hidden="true"></i>
        {{ failed ? 'This image couldn\\'t be loaded. Upload it again from the settings panel.' : 'No image yet. Upload one from the settings panel.' }}
      </div>
    }
  `,
  styles: [`
    :host { display: block; }
    .image-body {
      display: block;
      max-width: 100%;
      height: auto;
      margin: 0 auto;
      border-radius: var(--ecr-radius-sm, 6px);
    }
    .image-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 120px;
      border: 1px dashed var(--ecr-line, #dbe4e8);
      border-radius: var(--ecr-radius-sm, 6px);
    }
  `],
})
export class ImageBlockComponent implements ReportBlock, OnChanges, OnDestroy {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  imageId = '';
  altText = '';
  url: string | null = null;
  failed = false;

  private readonly imageId$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  // Optional, like the heatmap's: drawn where no page provides the store, there is simply no picture.
  constructor(@Optional() images: HeatmapImageStore | null) {
    this.imageId$.pipe(
      distinctUntilChanged(),
      switchMap(imageId => {
        this.url = null;
        this.failed = false;
        return imageId && images ? images.imageFor('', imageId) : of(null);
      }),
      takeUntil(this.destroy$),
    ).subscribe(url => {
      this.url = url;
      this.failed = !!this.imageId && !url;
    });
  }

  ngOnChanges() {
    if (this.block.reportType !== 'image') return;
    this.imageId = this.block.reportExtraData.imageId ?? '';
    this.altText = this.block.reportExtraData.altText ?? '';
    this.imageId$.next(this.imageId);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
